import type { EntityTypeSlug } from '../../api/queries/entities';

export interface CharacterFieldConfig {
  /** The descriptor category string stored in the DB */
  category: string;
  /** Human-readable label for the field */
  label: string;
  /** Rendering type */
  fieldType: 'combobox' | 'integer';
  /** Static options for combobox fields without taxonomy */
  staticOptions?: string[];
  /** Taxonomy category key — when set, options are loaded from API */
  taxonomyCategoryKey?: string;
  /** Unit label displayed as InputAdornment (e.g., "cm", "kg") */
  unit?: string;
  /** Default value pre-filled for new characters */
  defaultValue?: string;
  /** Whether this field allows multiple descriptor rows for the same category */
  multiValue: boolean;
  /** Visual group for layout sectioning */
  group: 'identity' | 'physical' | 'personality' | 'abilities';
  /** Grid column span (1 or 2, for full-width fields) */
  colSpan?: 1 | 2;
}

export const CHARACTER_STRUCTURED_FIELDS: CharacterFieldConfig[] = [
  // --- Identity ---
  {
    category: 'gender',
    label: 'Gender',
    fieldType: 'combobox',
    staticOptions: ['Male', 'Female', 'Non-binary', 'Other'],
    multiValue: false,
    group: 'identity',
  },
  {
    category: 'species',
    label: 'Species',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'species_humanoid',
    defaultValue: 'Human',
    multiValue: false,
    group: 'identity',
  },
  {
    category: 'sexual_orientation',
    label: 'Sexual Orientation',
    fieldType: 'combobox',
    staticOptions: [
      'Straight',
      'Gay/Lesbian',
      'Bisexual',
      'Pansexual',
      'Asexual',
      'Other',
    ],
    multiValue: false,
    group: 'identity',
  },
  {
    category: 'character_archetype',
    label: 'Archetype',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'character_archetypes',
    multiValue: false,
    group: 'identity',
  },

  // --- Physical ---
  {
    category: 'height',
    label: 'Height',
    fieldType: 'integer',
    unit: 'cm',
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'weight',
    label: 'Weight',
    fieldType: 'integer',
    unit: 'kg',
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'body_type',
    label: 'Body Type',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'body_types',
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'skin_color',
    label: 'Skin Color',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'skin_tones',
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'eye_color',
    label: 'Eye Color',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'eye_colors',
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'hair_color',
    label: 'Hair Color',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'hair_colors',
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'hair_length',
    label: 'Hair Length',
    fieldType: 'combobox',
    staticOptions: ['Bald', 'Buzz', 'Short', 'Medium', 'Long', 'Very Long'],
    multiValue: false,
    group: 'physical',
  },
  {
    category: 'hair_style',
    label: 'Hair Style',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'hair_styles',
    multiValue: false,
    group: 'physical',
  },

  // --- Personality (multi-value) ---
  {
    category: 'virtues',
    label: 'Virtues & Strengths',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'traits_positive',
    multiValue: true,
    group: 'personality',
    colSpan: 2,
  },
  {
    category: 'flaws',
    label: 'Flaws & Weaknesses',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'traits_negative',
    multiValue: true,
    group: 'personality',
    colSpan: 2,
  },

  // --- Abilities (multi-value) ---
  {
    category: 'magic_powers',
    label: 'Magic & Powers',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'magic_abilities',
    multiValue: true,
    group: 'abilities',
    colSpan: 2,
  },
  {
    category: 'physical_abilities',
    label: 'Physical Abilities',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'physical_abilities',
    multiValue: true,
    group: 'abilities',
    colSpan: 2,
  },
  {
    category: 'languages',
    label: 'Languages',
    fieldType: 'combobox',
    taxonomyCategoryKey: 'languages_human',
    multiValue: true,
    group: 'abilities',
    colSpan: 2,
  },
];

/** Categories managed by structured fields — excluded from free-form section */
export const STRUCTURED_CATEGORIES = new Set(
  CHARACTER_STRUCTURED_FIELDS.map((f) => f.category),
);

/** Free-form categories that keep the existing card-based rendering */
export const CHARACTER_FREEFORM_CATEGORIES = [
  'appearance',
  'voice',
  'attire',
  'personality',
  'background',
  'motivations',
];

/** Group labels for display */
export const GROUP_LABELS: Record<CharacterFieldConfig['group'], string> = {
  identity: 'Identity',
  physical: 'Physical',
  personality: 'Personality',
  abilities: 'Abilities',
};

/** Check whether the structured fields feature applies for this entity type */
export function hasStructuredFields(entityType: EntityTypeSlug): boolean {
  return entityType === 'character';
}
